import React, { FC, useState } from 'react';
import { TFunction } from 'react-i18next';
import { Save32, DataShare32, Renew24 } from '@carbon/icons-react';
import { Button } from '@mercell/button-react';
import { Dropdown } from '@mercell/dropdown-react';
import Textarea from '@mercell/textarea-react';
import { toast } from 'react-toastify';
import { KeyedMutator } from 'swr';
import { bulkSaveFormStatus } from '../../apiCalls/bulkSaveFormStatus';
import { bulkUpgradeForms } from '../../apiCalls/bulkUpgradeForms';
import { bulkSendStatusDownstream } from '../../apiCalls/bulkSendStatusDownstream';
import { BulkFormActionResult } from '../../types/bulkFormActionResult';
import { DropdownOption } from '../../types/generated/dropdownOption';
import { FormStatusResult } from '../../types/generated/formStatusResult';
import { SelectOption } from '../../types/generated/selectOption';

const maxBulkFormIds = 200;

interface BulkOperationsTabProps {
    t: TFunction;
    statuses?: DropdownOption[];
    mutateFormStatuses?: KeyedMutator<FormStatusResult>;
}

const BulkOperationsTab: FC<BulkOperationsTabProps> = ({
    t,
    statuses,
    mutateFormStatuses,
}) => {
    const [bulkFormIdsInput, setBulkFormIdsInput] = useState<string>('');
    const [bulkStatus, setBulkStatus] = useState<SelectOption | null>(null);

    const parseBulkFormIds = () =>
        bulkFormIdsInput
            .split(/[,\n]+/)
            .map((id) => id.trim())
            .filter(Boolean);

    const bulkFormIds = parseBulkFormIds();
    const bulkFormIdCount = bulkFormIds.length;
    const isBulkFormIdCountValid =
        bulkFormIdCount > 0 && bulkFormIdCount <= maxBulkFormIds;
    const canApplyStatus = isBulkFormIdCountValid && !!bulkStatus?.value?.toString();

    const getBulkResultToastText = (result: BulkFormActionResult) =>
        t('toast-content.bulk-action:summary', {
            succeeded: result.succeeded?.length ?? 0,
            failed: result.failed?.length ?? 0,
            skipped: result.skipped?.length ?? 0,
        });

    const showBulkResultToast = (result: BulkFormActionResult) => {
        const text = getBulkResultToastText(result);
        const failedCount = result.failed?.length ?? 0;
        const succeededCount = result.succeeded?.length ?? 0;

        if (failedCount > 0 && succeededCount === 0) {
            toast.error(text);
        } else if (failedCount > 0) {
            toast.warning(text);
        } else {
            toast.success(text);
        }
    };

    const onBulkSaveFormStatus = async () => {
        if (!canApplyStatus) {
            return;
        }

        const result = await bulkSaveFormStatus(
            bulkFormIds,
            Number(bulkStatus?.value),
            {
                onPendingText: t('toast-content.toast:saving'),
            }
        );
        showBulkResultToast(result);
        if (mutateFormStatuses) {
            mutateFormStatuses();
        }
    };

    const onBulkFormUpgrade = async () => {
        if (!isBulkFormIdCountValid) {
            return;
        }

        const result = await bulkUpgradeForms(bulkFormIds, {
            onPendingText: t('toast-content.toast:saving'),
        });
        showBulkResultToast(result);
    };

    const onBulkSendStatusDownstream = async () => {
        if (!isBulkFormIdCountValid) {
            return;
        }

        const result = await bulkSendStatusDownstream(bulkFormIds, {
            onPendingText: t('toast-content.toast:saving'),
        });
        showBulkResultToast(result);
    };

    return (
        <div
            className="w-full max-h-[calc(100vh-16rem)] space-y-5 overflow-y-auto [scrollbar-gutter:stable]"
            data-test="bulk-operations-section"
        >
            <p className="text-gray-600">
                {t('form-content.bulk-operations:description')}
            </p>

            <div>
                <label htmlFor="bulkFormIds">{t('form-content.bulk-operations:labelFormIds')}</label>
                <Textarea
                    id="bulkFormIds"
                    name="bulkFormIds"
                    className="mt-4 min-h-[180px] w-full resize-y font-mono text-sm"
                    placeholder={t('form-content.bulk-operations:placeholderFormIds')}
                    value={bulkFormIdsInput}
                    onChange={(e) => setBulkFormIdsInput(e.target.value)}
                    data-test="bulk-form-ids-input"
                />
                <p className="mt-2 text-xs text-gray-500">
                    {t('form-content.bulk-operations:hintFormIds', { count: bulkFormIdCount })}
                </p>
                {bulkFormIdCount > maxBulkFormIds && (
                    <p className="mt-1 text-xs text-red-600">
                        {t('form-content.bulk-operations:validationFormIdsMax', { max: maxBulkFormIds })}
                    </p>
                )}
            </div>

            {statuses && (
                <div className="w-4/6">
                    <label htmlFor="bulkStatus">{t('form-content.bulk-operations:labelFormStatus')}</label>
                    <Dropdown
                        id="bulkStatus"
                        name="bulkStatus"
                        className="mt-4"
                        menuPosition="fixed"
                        value={bulkStatus}
                        menuPlacement="bottom"
                        onChange={(newValue) => {
                            setBulkStatus(newValue as SelectOption | null);
                        }}
                        options={statuses.map((option: DropdownOption) => ({
                            value: option.value,
                            label: t(`list.audit-log-status:${option.name}`),
                        }))}
                        placeholder={t('form-content.bulk-operations:labelFormStatusPlaceholder')}
                    />
                </div>
            )}

            <div className="flex flex-wrap gap-5">
                <Button
                    iconSettings={{ Icon: Save32 }}
                    scheme="primary"
                    disabled={!canApplyStatus}
                    onClick={() => onBulkSaveFormStatus()}
                    title={t('form-content.bulk-operations:labelApplyStatus')}
                    data-test="bulk-status-button"
                >
                    {t('form-content.bulk-operations:labelApplyStatus')}
                </Button>
                <Button
                    iconSettings={{ Icon: Renew24 }}
                    scheme="secondary"
                    disabled={!isBulkFormIdCountValid}
                    onClick={() => onBulkFormUpgrade()}
                    title={t('form-content.bulk-operations:labelUpgrade')}
                    data-test="bulk-upgrade-button"
                >
                    {t('form-content.bulk-operations:labelUpgrade')}
                </Button>
                <Button
                    iconSettings={{ Icon: DataShare32 }}
                    scheme="tertiary"
                    disabled={!isBulkFormIdCountValid}
                    onClick={() => onBulkSendStatusDownstream()}
                    title={t('form-content.bulk-operations:labelSendStatusDownstream')}
                    data-test="bulk-send-status-downstream-button"
                >
                    {t('form-content.bulk-operations:labelSendStatusDownstream')}
                </Button>
            </div>
        </div>
    );
};

export default BulkOperationsTab;
